import Dot from 'features/BookingDetails/Dot'
import DotChecked from 'features/BookingDetails/DotChecked'
import DotCrossed from 'features/BookingDetails/DotCrossed'
import DotFilled from 'features/BookingDetails/DotFilled'
import usePlausible from 'hooks/usePlausible'
import moment from 'moment'
import { useEffect } from 'react'
import { formatPrice } from 'utils/common'

import styles from './Reservation.module.scss'

const Detail = ({ label, value }) => (
  <div className={styles.detail}>
    <div className={styles.detailLabel}>{label}</div>
    <div className={styles.detailValue} title={value}>
      {value}
    </div>
  </div>
)

const Step = ({ type = 'regular', title, date }) => (
  <div className={styles.step}>
    {type === 'checked' && <DotChecked />}
    {type === 'crossed' && <DotCrossed />}
    {type === 'filled' && <DotFilled />}
    {type === 'regular' && <Dot />}
    <p className={styles.stepTitle}>{title}</p>
    {date && <p className={styles.stepDate}>{moment(date).format('MMM DD, YYYY')}</p>}
  </div>
)

function getStepsCount(cancellationDate, startTimelineFromInvite) {
  let stepCount = 3

  if (startTimelineFromInvite) {
    stepCount++
  }

  if (cancellationDate) {
    stepCount++
  }

  return stepCount
}

const Reservation = ({
  bookedBy,
  bookingSource,
  trackingInfo,
  comments,
  rateCode,
  roomNumber,
  roomBookingCode,
  totalRate,
  checkIn,
  checkOut,
  roomName,
  bookingDate,
  adults,
  children,
  confirmationCode,
  refundPaymentAmount,
  agencyParentName,
  agencyMatchKind,
  guestFirstName,
  guestLastName,
  companyName,
  agencyName,
  startTimelineFromInvite = false,
  cancellationDate = null,
  invitedOn,
  nightlyRate,
  subscriberHotelBookingId
}) => {
  const stayLength = moment(checkOut).diff(checkIn, 'day')

  const guestsText = `${adults} adult${adults > 1 ? 's' : ''}${
    children > 0 ? `${children} ${children > 1 ? 'children' : 'child'} ` : ''
  }`

  const { postEvent, plausibleEvents } = usePlausible()

  useEffect(() => {
    postEvent(plausibleEvents.ViewReservationDetail, {
      subscriberHotelBookingId
    })
  }, [postEvent, plausibleEvents.ViewReservationDetail, subscriberHotelBookingId])

  const stepCount = getStepsCount(cancellationDate, startTimelineFromInvite)

  return (
    <div className={styles.reservationCard}>
      <div className={styles.lines}>
        <div className={styles.line} />
        <div className={styles.lineGood} style={{ width: `${100 / (stepCount - 1)}%` }}>
          {!startTimelineFromInvite && (
            <span className={styles.nightsLabel}>{stayLength} nights</span>
          )}
        </div>
      </div>
      <div className={styles.timeline}>
        {startTimelineFromInvite && <Step title="Invited on" date={invitedOn} />}
        <Step title="Booking" date={bookingDate} />
        {cancellationDate && (
          <Step type="crossed" title="Cancellation" date={cancellationDate} />
        )}
        <Step type="filled" title="Check in" date={checkIn} />
        <Step type="filled" title="Check out" date={checkOut} />
      </div>
      <div className={styles.details}>
        <div className={styles.detailColumn}>
          <Detail label="Guest name" value={`${guestFirstName} ${guestLastName}`} />
          <Detail label="Booked by" value={bookedBy} />
          <Detail label="Booking source" value={bookingSource} />
          <Detail label="Tracking info" value={trackingInfo} />
          <Detail label="Comments" value={comments} />
          <Detail label="Confirmation code" value={confirmationCode} />
          <Detail label="Agency parent" value={agencyParentName || '-'} />
          <Detail label="Agency name" value={agencyName || '-'} />
          <Detail
            label="Agency match kind"
            value={agencyMatchKind === 'NoMatching' ? '-' : agencyMatchKind}
          />
        </div>
        <div className={styles.detailColumn}>
          <Detail label="Room number" value={roomNumber} />
          <Detail label="Room name" value={roomName} />
          <Detail label="Room code" value={roomBookingCode} />
          <Detail label="Guests" value={guestsText} />
          <Detail label="Rate code" value={rateCode} />
          <Detail label="Nightly rate" value={formatPrice(nightlyRate)} />
          <Detail label="Total rate" value={formatPrice(totalRate)} />
          <Detail label="Company name" value={companyName} />
          {refundPaymentAmount && refundPaymentAmount > 0 ? (
            <Detail label="Refund amount" value={formatPrice(refundPaymentAmount)} />
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default Reservation
